"use client";

import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { useLightbox } from "@/hooks/useLightbox";

interface GalleryDisplayProps {
  images: Array<{
    src: string;
    thumbnailSrc: string;
    fullSrc: string;
    alt: string;
    title?: string;
    width?: number;
    height?: number;
  }>;
  allImages: Array<{
    src: string;
    thumbnailSrc: string;
    fullSrc: string;
    alt: string;
    title?: string;
    width?: number;
    height?: number;
  }>;
  currentPage: number;
  totalImages: number;
  imagesPerPage: number;
  error?: string | null;
}

// Helper to get image dimensions from filename if available
function getImageSize(src: string) {
  const match = src.match(/-(\d+)x(\d+)\.webp$/);
  if (match) {
    return { width: parseInt(match[1]), height: parseInt(match[2]) };
  }
  // Default fallback
  return { width: 800, height: 600 };
}

export default function GalleryDisplay({
  images,
  allImages,
  currentPage,
  totalImages,
  imagesPerPage,
  error,
}: GalleryDisplayProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [imagesPerPageState, setImagesPerPageState] = useState(imagesPerPage);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isProgrammaticScrollRef = useRef(false);

  const totalPages = Math.ceil(totalImages / imagesPerPageState);
  
  // Use allImages for lightbox, images for grid
  const lightboxImages = allImages.length > 0 ? allImages : images;

  const handleOpen = (idx: number) => {
    // Calculate the actual index in the full image array
    // idx is the index within the current page's images
    const pageOffset = (currentPage - 1) * imagesPerPageState;
    const actualIndex = pageOffset + idx;
    setActiveIndex(actualIndex);
    setLightboxOpen(true);
  };

  const handleClose = () => setLightboxOpen(false);

  // Scroll to active image with smooth animation
  const scrollToIndex = (index: number) => {
    if (!scrollContainerRef.current) return;
    isProgrammaticScrollRef.current = true;
    const container = scrollContainerRef.current;
    const scrollWidth = container.clientWidth;
    container.scrollTo({
      left: index * scrollWidth,
      behavior: 'smooth'
    });
    
    // Reset flag after scroll completes
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 500);
  };

  // Update active index based on scroll position with debouncing
  useEffect(() => {
    if (!lightboxOpen || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    
    const handleScroll = () => {
      // Ignore scroll events from programmatic scrolling
      if (isProgrammaticScrollRef.current) return;
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce scroll updates to prevent jitter
      scrollTimeoutRef.current = setTimeout(() => {
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.clientWidth;
        const calculatedIndex = Math.round(scrollLeft / scrollWidth);
        
        // Only update if index actually changed and is valid
        if (calculatedIndex !== activeIndex && 
            calculatedIndex >= 0 && 
            calculatedIndex < lightboxImages.length) {
          setActiveIndex(calculatedIndex);
        }
      }, 150); // 150ms debounce
    };

    // Use scrollend for better snap detection (modern browsers)
    const handleScrollEnd = () => {
      if (isProgrammaticScrollRef.current) return;
      
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.clientWidth;
      const calculatedIndex = Math.round(scrollLeft / scrollWidth);
      
      if (calculatedIndex !== activeIndex && 
          calculatedIndex >= 0 && 
          calculatedIndex < images.length) {
        setActiveIndex(calculatedIndex);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    // scrollend is not fully typed yet in TypeScript
    container.addEventListener('scrollend', handleScrollEnd, { passive: true } as AddEventListenerOptions);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('scrollend', handleScrollEnd);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [lightboxOpen, activeIndex, images.length]);

  // Scroll to active image when it changes (from button clicks)
  useEffect(() => {
    if (lightboxOpen && scrollContainerRef.current) {
      scrollToIndex(activeIndex);
    }
  }, [activeIndex, lightboxOpen]);

  // Initial scroll position when lightbox opens
  useEffect(() => {
    if (lightboxOpen && scrollContainerRef.current) {
      // Instant scroll on open, no animation
      isProgrammaticScrollRef.current = true;
      const container = scrollContainerRef.current;
      const scrollWidth = container.clientWidth;
      container.scrollLeft = activeIndex * scrollWidth;
      
      // Reset flag immediately for instant scroll
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 100);
    }
    // Only run when lightbox opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  // Preload full-size image on hover for faster lightbox opening
  const handleImageHover = (idx: number) => {
    const img = new window.Image();
    img.src = images[idx].fullSrc;
  };

  // Use the reusable lightbox hook for scroll lock, escape, and popstate
  useLightbox(lightboxOpen, handleClose);

  // Keyboard navigation (arrow keys)
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % lightboxImages.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxImages.length]);

  // Initial preload when lightbox opens - load a batch of images
  useEffect(() => {
    if (!lightboxOpen || lightboxImages.length === 0) return;
    
    const preloadImage = (src: string, priority: boolean = false) => {
      const img = new window.Image();
      if (priority && 'fetchPriority' in img) {
        (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'high';
      }
      img.src = src;
    };
    
    // Preload current image with high priority
    if (lightboxImages[activeIndex]) preloadImage(lightboxImages[activeIndex].fullSrc, true);
    
    // On lightbox open, aggressively preload 30 images ahead and 30 behind for instant navigation
    for (let i = 1; i <= 30; i++) {
      const nextIdx = (activeIndex + i) % lightboxImages.length;
      const prevIdx = (activeIndex - i + lightboxImages.length) % lightboxImages.length;
      // First 10 images get high priority
      const isHighPriority = i <= 10;
      if (lightboxImages[nextIdx]) preloadImage(lightboxImages[nextIdx].fullSrc, isHighPriority);
      if (lightboxImages[prevIdx]) preloadImage(lightboxImages[prevIdx].fullSrc, isHighPriority);
    }
    
    // Continue preloading the rest in the background with low priority
    if (lightboxImages.length > 60) {
      setTimeout(() => {
        for (let i = 31; i <= Math.min(100, lightboxImages.length); i++) {
          const nextIdx = (activeIndex + i) % lightboxImages.length;
          const prevIdx = (activeIndex - i + lightboxImages.length) % lightboxImages.length;
          if (lightboxImages[nextIdx]) preloadImage(lightboxImages[nextIdx].fullSrc, false);
          if (lightboxImages[prevIdx]) preloadImage(lightboxImages[prevIdx].fullSrc, false);
        }
      }, 500);
    }
    // Only run when lightbox opens, not on activeIndex change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  // Preload adjacent images in lightbox for faster navigation
  useEffect(() => {
    if (!lightboxOpen || lightboxImages.length === 0) return;

    const preloadImage = (src: string, priority: boolean = false) => {
      const img = new window.Image();
      if (priority && 'fetchPriority' in img) {
        (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'high';
      }
      img.src = src;
    };

    // Aggressively preload next 20 and previous 20 images for instant navigation
    const imagesToPreload: { src: string; priority: boolean }[] = [];
    
    // Preload next images (first 5 with high priority)
    for (let i = 1; i <= 20; i++) {
      const nextIndex = (activeIndex + i) % lightboxImages.length;
      if (lightboxImages[nextIndex]) {
        imagesToPreload.push({ src: lightboxImages[nextIndex].fullSrc, priority: i <= 5 });
      }
    }
    
    // Preload previous images (first 5 with high priority)
    for (let i = 1; i <= 20; i++) {
      const prevIndex = (activeIndex - i + lightboxImages.length) % lightboxImages.length;
      if (lightboxImages[prevIndex] && prevIndex !== activeIndex) {
        imagesToPreload.push({ src: lightboxImages[prevIndex].fullSrc, priority: i <= 5 });
      }
    }
    
    // Preload all images in parallel with appropriate priority
    imagesToPreload.forEach(({ src, priority }) => preloadImage(src, priority));
  }, [lightboxOpen, activeIndex, lightboxImages]);

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", newPage.toString());
    params.set("limit", imagesPerPageState.toString());
    window.location.href = `/gallery?${params.toString()}`;
  };

  const handleLimitChange = (newLimit: number) => {
    setImagesPerPageState(newLimit);
    const params = new URLSearchParams(window.location.search);
    params.set("page", "1"); // Reset to first page
    params.set("limit", newLimit.toString());
    window.location.href = `/gallery?${params.toString()}`;
  };

  // Show error state
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8">
          Gallery
        </h1>
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Content Management System Setup Required
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
            <strong>For administrators:</strong> Please ensure Contentful is
            properly configured and accessible.
          </div>
        </div>
      </div>
    );
  }

  // Show empty state
  if (images.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8">
          Gallery
        </h1>
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            No Images Found
          </h3>
          <p className="text-gray-600">
            The gallery is empty. Please check back later for new images.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-12">
      <div className="flex flex-col gap-2 mb-8">
        <div className="flex flex-wrap justify-between items-center w-full mb-2 px-0 sm:px-2 md:px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-0">
            Gallery
          </h1>
          <div className="flex items-center gap-2 mt-2 sm:mt-0">
            <span className="text-sm text-gray-600">Images per page:</span>
            <select
              value={imagesPerPageState}
              onChange={(e) => handleLimitChange(parseInt(e.target.value))}
              className="border border-gray-300 rounded px-3 py-1 text-sm"
              aria-label="Images per page"
            >
              <option value={20}>20</option>
              <option value={40}>40</option>
              <option value={60}>60</option>
              <option value={80}>80</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Gallery stats */}
      <div className="mb-6">
        <p className="text-gray-600">
          {(() => {
            // Show correct range for mobile and larger screens
            const start = (currentPage - 1) * imagesPerPageState + 1;
            const end = Math.min(currentPage * imagesPerPageState, totalImages);
            return `Showing ${start}-${end} of ${totalImages} images`;
          })()}
        </p>
      </div>

      {/* Mobile: 2-column grid, smaller images; Larger: masonry columns */}
      <div>
        {/* Mobile layout - optimized for performance */}
        <div className="grid grid-cols-2 gap-2 sm:hidden">
          {images.map((image, idx) => (
            <button
              key={`${image.src}-${idx}`}
              className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-linear-to-br from-[#1a223a] via-[#232a3d] to-[#0e143e] w-full block mb-2 aspect-3/2"
              aria-label={`Enlarge image ${idx + 1}`}
              onClick={() => handleOpen(idx)}
              onMouseEnter={() => handleImageHover(idx)}
              onFocus={() => handleImageHover(idx)}
            >
              <Image
                src={image.thumbnailSrc}
                alt={image.alt}
                width={300}
                height={200}
                sizes="(max-width: 640px) 50vw"
                className={`object-cover${image.src === "/webp/118841222.webp" ? " w-auto h-auto" : " w-full h-auto"}`}
                loading={idx < 4 ? "eager" : "lazy"}
                placeholder="blur"
                blurDataURL="data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA="
              />
            </button>
          ))}
        </div>
        {/* Tablet and up: masonry columns */}
        <div className="hidden sm:block">
          <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-4 space-y-4">
            {images.map((image, idx) => {
              const { width, height } =
                image.width && image.height
                  ? { width: image.width, height: image.height }
                  : getImageSize(image.src);
              return (
                <button
                  key={`${image.src}-${idx}`}
                  className="break-inside-avoid rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-linear-to-br from-[#1a223a] via-[#232a3d] to-[#0e143e] w-full block mb-4"
                  aria-label={`Enlarge image ${idx + 1}`}
                  onClick={() => handleOpen(idx)}
                  onMouseEnter={() => handleImageHover(idx)}
                  onFocus={() => handleImageHover(idx)}
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    width={width}
                    height={height}
                    sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw"
                    className={`object-cover${image.src === "/webp/118841222.webp" ? " w-auto h-auto" : " w-full h-auto"}`}
                    loading={idx < 6 ? "eager" : "lazy"}
                    placeholder="blur"
                    blurDataURL="data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA="
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>

            <div className="flex gap-1">
              {/* Show page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`w-10 h-10 rounded-lg transition-colors ${
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white"
                        : "border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>

          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-80 transition-opacity duration-300"
          onClick={handleClose}
        >
          {/* Top blue bar - responsive */}
          <div
            className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-3 md:p-4 bg-school-navy"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-white text-base md:text-lg font-medium">
              {activeIndex + 1} of {lightboxImages.length}
            </div>
            <button
              className="text-white text-3xl md:text-4xl font-bold hover:text-yellow-400 transition-colors p-1 md:p-2"
              onClick={handleClose}
              aria-label="Close gallery"
            >
              ×
            </button>
          </div>

          {/* Horizontal scroll container with snap - Instagram/Facebook style */}
          <div
            ref={scrollContainerRef}
            className="gallery-scroll-container absolute inset-0 pt-16 pb-20 md:pb-16 lg:pb-14 overflow-x-auto overflow-y-hidden"
          >
            <div className="flex h-full" style={{ width: `${lightboxImages.length * 100}vw` }}>
              {lightboxImages.map((image, idx) => (
                <div
                  key={`lightbox-${idx}`}
                  className="gallery-scroll-item shrink-0 flex items-center justify-center px-2"
                  style={{ width: '100vw', height: '100%' }}
                  onClick={handleClose}
                >
                  <div
                    className="relative max-w-full max-h-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                      width: 'fit-content',
                      height: 'fit-content',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                  >
                    {/* Use native img for instant loading with preloaded images */}
                    <img
                      src={image.fullSrc}
                      alt={image.alt}
                      className="max-w-full max-h-full w-auto h-auto object-contain rounded"
                      loading={Math.abs(idx - activeIndex) <= 10 ? 'eager' : 'lazy'}
                      fetchPriority={Math.abs(idx - activeIndex) <= 5 ? 'high' : 'low'}
                      style={{ maxHeight: 'calc(100vh - 10rem)' }}
                      decoding="async"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prefetch links for aggressive preloading of surrounding images */}
          <div className="hidden">
            {[...Array(6)].map((_, i) => {
              const nextIdx = (activeIndex + i + 1) % lightboxImages.length;
              const prevIdx = (activeIndex - i - 1 + lightboxImages.length) % lightboxImages.length;
              return (
                <div key={`prefetch-${i}`}>
                  {lightboxImages[nextIdx] && (
                    <link
                      rel="prefetch"
                      as="image"
                      href={lightboxImages[nextIdx].fullSrc}
                      fetchPriority={i < 3 ? "high" : "low"}
                    />
                  )}
                  {lightboxImages[prevIdx] && prevIdx !== nextIdx && (
                    <link
                      rel="prefetch"
                      as="image"
                      href={lightboxImages[prevIdx].fullSrc}
                      fetchPriority={i < 3 ? "high" : "low"}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom blue bar - reorganized layout for larger screens */}
          <div
            className="absolute bottom-0 left-0 right-0 z-10 p-2 md:p-3 bg-school-navy"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile/tablet layout: thumbnails above buttons */}
            <div className="lg:hidden">
              {/* Compact thumbnail strip */}
              <div className="flex gap-1 md:gap-2 justify-center overflow-x-auto pb-1 md:pb-2 mb-2">
                {(() => {
                  // Show fewer thumbnails on mobile (6), more on larger screens (10)
                  const maxThumbs = 10; // We'll handle mobile with CSS hiding
                  const startIdx = Math.max(
                    0,
                    activeIndex - Math.floor(maxThumbs / 2),
                  );
                  const endIdx = Math.min(images.length, startIdx + maxThumbs);
                  const visibleImages = images.slice(startIdx, endIdx);

                  return visibleImages.map((image, idx) => {
                    const actualIdx = startIdx + idx;
                    return (
                      <button
                        key={actualIdx}
                        className={`w-10 h-10 md:w-12 md:h-12 relative border-2 shrink-0 ${
                          activeIndex === actualIdx
                            ? "border-yellow-400"
                            : "border-transparent"
                        } rounded overflow-hidden hover:border-yellow-200 transition-colors ${
                          idx >= 6 ? "hidden sm:block" : "" // Hide thumbnails 7-10 on mobile
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveIndex(actualIdx);
                        }}
                        aria-label={`View image ${actualIdx + 1}`}
                      >
                        <Image
                          src={image.thumbnailSrc}
                          alt={image.alt}
                          fill
                          className="object-cover"
                          sizes="(max-width: 640px) 40px, 48px"
                          loading="lazy"
                        />
                      </button>
                    );
                  });
                })()}

                {/* Show indicator if there are more images - responsive text */}
                {lightboxImages.length > 6 && (
                  <div className="flex items-center px-2 md:px-3 text-gray-300 text-xs md:text-sm whitespace-nowrap">
                    <span className="sm:hidden">+{lightboxImages.length - 6} more</span>
                    <span className="hidden sm:inline">
                      +{lightboxImages.length - 10} more
                    </span>
                  </div>
                )}
              </div>

              {/* Navigation buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-between w-full">
                <button
                  className="w-full sm:w-auto text-black text-sm md:text-base px-3 md:px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex(
                      (activeIndex - 1 + lightboxImages.length) % lightboxImages.length,
                    );
                  }}
                  disabled={lightboxImages.length <= 1}
                  aria-label="Previous image"
                >
                  ← Previous
                </button>
                <button
                  className="w-full sm:w-auto text-black text-sm md:text-base px-3 md:px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex((activeIndex + 1) % lightboxImages.length);
                  }}
                  disabled={lightboxImages.length <= 1}
                  aria-label="Next image"
                >
                  Next →
                </button>
              </div>
            </div>

            {/* Desktop layout: single row with thumbnails between buttons */}
            <div className="hidden lg:flex items-center justify-between gap-4">
              {/* Previous button */}
              <button
                className="text-black text-base px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex(
                    (activeIndex - 1 + lightboxImages.length) % lightboxImages.length,
                  );
                }}
                disabled={lightboxImages.length <= 1}
                aria-label="Previous image"
              >
                ← Previous
              </button>

              {/* Thumbnails in center */}
              <div className="flex gap-2 justify-center overflow-x-auto flex-1 max-w-2xl">
                {(() => {
                  const maxThumbs = 8; // Fewer for inline layout
                  const startIdx = Math.max(
                    0,
                    activeIndex - Math.floor(maxThumbs / 2),
                  );
                  const endIdx = Math.min(lightboxImages.length, startIdx + maxThumbs);
                  const visibleImages = lightboxImages.slice(startIdx, endIdx);

                  return visibleImages.map((image, idx) => {
                    const actualIdx = startIdx + idx;
                    return (
                      <button
                        key={actualIdx}
                        className={`w-12 h-12 relative border-2 shrink-0 ${
                          activeIndex === actualIdx
                            ? "border-yellow-400"
                            : "border-transparent"
                        } rounded overflow-hidden hover:border-yellow-200 transition-colors`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveIndex(actualIdx);
                        }}
                        aria-label={`View image ${actualIdx + 1}`}
                      >
                        <Image
                          src={image.thumbnailSrc}
                          alt={image.alt}
                          fill
                          className="object-cover"
                          sizes="48px"
                          loading="lazy"
                        />
                      </button>
                    );
                  });
                })()}

                {/* Show indicator if there are more images */}
                {lightboxImages.length > 8 && (
                  <div className="flex items-center px-2 text-gray-300 text-sm whitespace-nowrap">
                    +{lightboxImages.length - 8} more
                  </div>
                )}
              </div>

              {/* Next button */}
              <button
                className="text-black text-base px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex((activeIndex + 1) % lightboxImages.length);
                }}
                disabled={lightboxImages.length <= 1}
                aria-label="Next image"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
